"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { AddInstituteOffcanvas } from "./add"
import { EditInstituteOffcanvas } from "./edit"
import { InstitutesList, type InstituteFilters } from "./list"
import api from "../../../libs/axios"
import { TrainingInstitute, Pagination } from "../types"
import type { SelectOption } from "../../../utils/select-with-search"
import { useDebounce } from "../../../hooks/useDebounce"

interface CenterFormData {
  id?: number
  center_name: string
  contact_person?: string
  contact_phone?: string
  contact_email?: string
  address?: string
}

interface InstituteFormData {
  institute_name: string
  institute_short_name?: string
  address?: string
  contact_person?: string
  contact_phone?: string
  contact_email?: string
  gst_number?: string
  centers?: CenterFormData[]
}

interface EditInstituteFormData extends InstituteFormData {
  id: number
}

export const TrainingInstitutesManagement: React.FC = () => {
  const [institutes, setInstitutes] = useState<TrainingInstitute[]>([])
  const [pagination, setPagination] = useState<Pagination>({
    offset: 0,
    limit: 10,
    page: 1,
    total_pages: 1,
    total_count: 0,
    has_next_page: false,
    has_prev_page: false,
    next_page: null,
    prev_page: null,
  })

  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [showAdd, setShowAdd] = useState(false)
  const [showEdit, setShowEdit] = useState(false)
  const [selectedInstitute, setSelectedInstitute] = useState<TrainingInstitute | null>(null)
  const [searchTerm, setSearchTerm] = useState("")
  const debouncedSearch = useDebounce(searchTerm, 400)
  const [statusFilter, setStatusFilter] = useState<string>("")
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: "ASC" | "DESC" }>({
    field: "created_at",
    direction: "DESC",
  })
  const [advancedFilters, setAdvancedFilters] = useState<InstituteFilters>({})
  const [instituteOptions, setInstituteOptions] = useState<SelectOption[]>([])

  const fetchInstitutes = async (
    search = "",
    status = "",
    page = 1,
    limitValue?: number,
    sort = sortConfig,
    filters = advancedFilters,
  ) => {
    setLoading(true)
    try {
      const effectiveLimit = limitValue ?? pagination.limit ?? 10
      const queryParams: any = { page, limit: effectiveLimit }
      if (search.trim()) queryParams.search = search
      if (status) queryParams.is_active = status
      if (sort?.field) {
        queryParams.sort_by = sort.field
        queryParams.sort_order = sort.direction
      }
      Object.entries(filters || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== "") {
          queryParams[key] = value
        }
      })

      const response = await api.get("/training/institutes/list", { params: queryParams })
      if (response.data.success) {
        setInstitutes(response.data.data.institutes || [])
        setPagination(response.data.data.pagination || pagination)
        const fetchedInstitutes: TrainingInstitute[] = response.data.data.institutes || []
        if (fetchedInstitutes.length) {
          setInstituteOptions((prev) => {
            const optionMap = new Map<string, SelectOption>()
            prev.forEach((option) => optionMap.set(String(option.value), option))
            fetchedInstitutes.forEach((inst) => {
              if (inst.institute_name) {
                optionMap.set(inst.institute_name, {
                  id: inst.id,
                  label: inst.institute_name,
                  value: inst.institute_name,
                })
              }
            })
            return Array.from(optionMap.values())
          })
        }
      }
    } catch (error) {
      console.error("Error fetching institutes:", error)
    } finally {
      setLoading(false)
    }
  }

  const createInstitute = async (data: InstituteFormData) => {
    setActionLoading(true)
    try {
      const response = await api.post("/training/institutes/create", data)
      if (response.data.success) {
        await fetchInstitutes(searchTerm, statusFilter, 1, undefined, sortConfig, advancedFilters)
        setShowAdd(false)
      }
    } catch (error) {
      console.error("Error creating institute:", error)
    } finally {
      setActionLoading(false)
    }
  }

  const updateInstitute = async (data: EditInstituteFormData) => {
    setActionLoading(true)
    try {
      const response = await api.put(`/training/institutes/${data.id}`, data)
      if (response.data.success) {
        await fetchInstitutes(searchTerm, statusFilter, pagination.page, undefined, sortConfig, advancedFilters)
        setShowEdit(false)
        setSelectedInstitute(null)
      }
    } catch (error) {
      console.error("Error updating institute:", error)
    } finally {
      setActionLoading(false)
    }
  }

  const toggleStatus = async (institute: TrainingInstitute) => {
    setActionLoading(true)
    try {
      const response = await api.patch(`/training/institutes/${institute.id}/status`, {
        is_active: !institute.is_active,
      })
      if (response.data.success) {
        await fetchInstitutes(searchTerm, statusFilter, pagination.page, undefined, sortConfig, advancedFilters)
      }
    } catch (error) {
      console.error("Error toggling institute status:", error)
    } finally {
      setActionLoading(false)
    }
  }

  const handleSearch = (search: string) => {
    setSearchTerm(search)
    // API call triggered by debouncedSearch effect below
  }

  // Fire fetch when debounced search value settles
  useEffect(() => {
    fetchInstitutes(debouncedSearch, statusFilter, 1, undefined, sortConfig, advancedFilters)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status)
    fetchInstitutes(searchTerm, status, 1, undefined, sortConfig, advancedFilters)
  }

  const handlePageChange = (page: number) => {
    fetchInstitutes(searchTerm, statusFilter, page, undefined, sortConfig, advancedFilters)
  }

  const handleLimitChange = (limit: number) => {
    setPagination((prev) => ({ ...prev, limit, page: 1 }))
    fetchInstitutes(searchTerm, statusFilter, 1, limit, sortConfig, advancedFilters)
  }

  const handleSort = (field: string, direction: "ASC" | "DESC") => {
    const sort = { field, direction }
    setSortConfig(sort)
    fetchInstitutes(searchTerm, statusFilter, pagination.page, undefined, sort, advancedFilters)
  }

  const handleAdvancedFilter = (filters: InstituteFilters) => {
    setAdvancedFilters(filters)
    fetchInstitutes(searchTerm, statusFilter, 1, undefined, sortConfig, filters)
  }

  const handleEdit = (institute: TrainingInstitute) => {
    setSelectedInstitute(institute)
    setShowEdit(true)
  }

  const handleCloseModals = () => {
    setShowAdd(false)
    setShowEdit(false)
    setSelectedInstitute(null)
  }

  useEffect(() => {
    fetchInstitutes()
  }, [])

  return (
    <div>
      <InstitutesList
        institutes={institutes}
        pagination={pagination}
        onSearch={handleSearch}
        onStatusFilter={handleStatusFilter}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        onAdd={() => setShowAdd(true)}
        onEdit={handleEdit}
        onToggleStatus={toggleStatus}
        loading={loading}
        sortConfig={sortConfig}
        onSort={handleSort}
        onAdvancedFilter={handleAdvancedFilter}
        advancedFilters={advancedFilters}
        instituteOptions={instituteOptions}
      />

      <AddInstituteOffcanvas
        isOpen={showAdd}
        onClose={handleCloseModals}
        onSubmit={createInstitute}
        loading={actionLoading}
      />

      <EditInstituteOffcanvas
        isOpen={showEdit}
        onClose={handleCloseModals}
        onSubmit={updateInstitute}
        institute={selectedInstitute}
        loading={actionLoading}
      />
    </div>
  )
}
